<script lang="ts">
export default {
  name: 'ClientViewOverlay',
};
</script>

<script setup lang="ts">
import { X, ChevronsDown } from 'lucide-vue-next';
import { CollapsibleContent, CollapsibleRoot, CollapsibleTrigger } from 'reka-ui';
import { AppState, Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { UserPermission } from '@/types/UserPermissions';
import ConfirmModal from '@/components/v2/ConfirmModal.vue';

const store = useStore<AppState>();
const router = useRouter();
const route = useRoute();
const returnRoute = ref('');
const returnQuery = ref({});
const isOpen = ref(false);
const collapsibleRef = ref<HTMLDivElement | null>(null);
const confirmModalRef = ref<InstanceType<typeof ConfirmModal> | null>(null);

// List of permissions for the collapsible dropdown
const permissions = ref([
  { id: UserPermission.OverviewRead, label: 'Overview', checked: false },
  { id: UserPermission.PoliciesRead, label: 'Policies', checked: false },
  { id: UserPermission.TowersRead, label: 'Towers', checked: false },
  { id: UserPermission.ProgramsRead, label: 'Programs', checked: false },
  { id: UserPermission.DocumentsRead, label: 'Documents', checked: false },
  { id: UserPermission.RenewalRead, label: 'Renewals', checked: false },
  { id: UserPermission.AnalyticsRead, label: 'Analytics', checked: false },
  { id: UserPermission.NotificationsRead, label: 'Notifications', checked: false },
  { id: UserPermission.DocumentsWrite, label: 'Document Upload', checked: false },
  { id: UserPermission.ClaimsRead, label: 'Claims', checked: false },
  { id: UserPermission.CardviewRead, label: 'Card View', checked: false },
]);

// set the permissions that are in the user's permissions array
for (const userPermission of store.state.clientViewState.permissions) {
  for (const permission of permissions.value) {
    if (permission.id === userPermission) {
      permission.checked = true;
    }
  }
}

// when entering client view, redirect the user to the appropriate route
const routeMap = new Map<string, string>([
  ['ClientView', 'Home'],
  ['AccountList', 'Home'],
  ['account-benchmarking', 'account-overview'],
  ['account-benchmarking-selected', 'account-overview'],
  ['account-tower-detail', 'account-overview'],
  ['account-towers', 'account-overview'],
  ['account-exposure', 'account-overview'],
  ['account-claim-detail', 'account-overview'],
  ['account-cyber', 'account-overview'],
  ['account-quotes', 'account-overview'],
  ['account-quote-program', 'account-overview'],
  ['account-renewal', 'account-overview'],
  ['account-client-readiness', 'account-overview'],
  ['account-analytics', 'account-overview'],
]);

async function handleClientViewClose() {
  // Show confirmation dialog before exiting client view
  const confirmed = await confirmModalRef.value?.showConfirm('Are you sure you want to exit Client View?');
  
  if (!confirmed) {
    return;
  }

  Commit(StoreMutationType.DISABLE_CLIENT_VIEW_MODE);

  const routeName = route.name?.toString();
  if (routeName && routeMap.has(routeName)) {
    router.push({ name: routeMap.get(routeName) });
  } else if (returnRoute.value && routeName === returnRoute.value) {
    // Redirect to the original route with params if still on this route
    router.push({ name: routeName, query: returnQuery.value });
  }
}

function handleClickOutside(event: MouseEvent) {
  if (collapsibleRef.value && !collapsibleRef.value.contains(event.target as Node)) {
    isOpen.value = false;
  }
}

function handleTogglePermission(permission: UserPermission, checked: boolean) {
  if (checked) {
    Commit(StoreMutationType.ADD_CLIENT_VIEW_PERMISSION, permission);
  } else {
    Commit(StoreMutationType.REMOVE_CLIENT_VIEW_PERMISSION, permission);
  }
}

onMounted(() => {
  returnRoute.value = route.name?.toString() || '';
  returnQuery.value = { ...route.query };

  const routeName = route.name?.toString();
  if (routeName && routeMap.has(routeName)) {
    router.push({ name: routeMap.get(routeName) });
  } else {
    router.push({ name: routeName, query: {} });
  }

  document.addEventListener('mousedown', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});
</script>

<template>
  <div
    v-if="store.state.clientViewState.isClientViewEnabled"
    class="tw:pointer-events-none tw:absolute tw:left-0 tw:top-0 tw:z-2000 tw:h-full tw:w-full">
    <div
      class="tw:sticky tw:top-0 tw:flex tw:h-screen tw:w-full tw:justify-center tw:border-4 tw:border-solid tw:border-green-500">
      <div ref="collapsibleRef" class="tw:pointer-events-auto tw:absolute tw:top-0">
        <CollapsibleRoot
          v-model:open="isOpen"
          class="tw:border tw:border-solid tw:border-green-500 tw:bg-green-500 tw:text-xs tw:text-black">
          <!-- Header with trigger and close button -->
          <div class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:px-2 tw:pb-1">
            <CollapsibleTrigger class="tw:flex tw:w-full tw:items-center tw:justify-center tw:gap-2">
              <ChevronsDown
                :size="14"
                class="tw:cursor-pointer tw:stroke-black tw:stroke-2 tw:transition-transform"
                :class="{ 'tw:rotate-180': isOpen }" />
              <span class="tw:font-medium">Client View</span>
            </CollapsibleTrigger>

            <div class="tw:tooltip tw:tooltip-bottom tw:tooltip-error" data-tip="Close Client View">
              <X
                :size="16"
                class="tw:cursor-pointer tw:stroke-red-900 tw:stroke-[3] tw:transition-transform tw:hover:scale-110"
                @click="handleClientViewClose" />
            </div>
          </div>

          <!-- Collapsible content with permissions -->
          <CollapsibleContent class="CollapsibleContent tw:border-t tw:border-green-600 tw:bg-green-200">
            <div class="tw:space-y-2 tw:px-3 tw:py-2">
              <div class="tw:text-xs tw:font-semibold tw:text-black/80">Permissions:</div>
              <div class="tw:grid tw:grid-cols-2 tw:gap-x-4 tw:gap-y-1">
                <label
                  v-for="permission in permissions"
                  :key="permission.id"
                  class="tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:rounded-sm tw:px-1 tw:py-0.5 tw:hover:bg-green-600/20">
                  <input
                    type="checkbox"
                    v-model="permission.checked"
                    @click="handleTogglePermission(permission.id, !permission.checked)"
                    class="tw:checkbox tw:checkbox-xs tw:border-solid tw:border-green-700 tw:checked:bg-green-700" />
                  <span class="tw:select-none tw:whitespace-nowrap tw:text-xs">{{ permission.label }}</span>
                </label>
              </div>
            </div>
          </CollapsibleContent>
        </CollapsibleRoot>
      </div>
    </div>
    
    <!-- Confirmation Modal -->
    <ConfirmModal ref="confirmModalRef" />
  </div>
</template>

<style>
.CollapsibleContent {
  overflow: hidden;
}
.CollapsibleContent[data-state='open'] {
  animation: slideDown 150ms ease-out;
}
.CollapsibleContent[data-state='closed'] {
  animation: slideUp 150ms ease-out;
}

@keyframes slideDown {
  from {
    height: 0;
  }
  to {
    height: var(--reka-collapsible-content-height);
  }
}

@keyframes slideUp {
  from {
    height: var(--reka-collapsible-content-height);
  }
  to {
    height: 0;
  }
}
</style>
